import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EditIcon,
  MapPinIcon,
  UserIcon,
  Loader2Icon,
  FilterIcon,
  XIcon,
  CalendarIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { formatDateInputValue } from '../lib/dateUtils'
import { formatAssigneeTeamLabel } from '../lib/jobAssignmentHistory'
import { useDivision } from '../contexts/DivisionContext'
import { recordMatchesDivision } from '../lib/operationalDivisions'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { resolveEmployeeIdForUser } from '../lib/timeClockActions'
import {
  fetchJobsByIds,
  resolveAssignedJobsScope,
  canViewAllJobs,
} from '../lib/jobVisibility'
import { isFieldCompleteStatus } from '../lib/jobStatusWorkflow'
import {
  EMPTY_CALENDAR_MONTH_STATS,
  fetchAllCalendarJobs,
  fetchCalendarMonthStats,
  type CalendarMonthStats,
} from '../api/jobCalendar'
import {
  mobile,
  MobilePage,
  MobilePageHeader,
  MobileStatStrip,
  MobileSelect,
  MobileEmptyState,
} from '../components/mobile'

interface CalendarJob {
  id: string
  job_number: string
  title: string
  scheduled_date: string
  status: string
  priority: string
  service_type?: string
  operational_division_id?: string | null
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  crew?: { name?: string } | null
  employee?: { first_name?: string; last_name?: string } | null
  teamLabel?: string
  customer?: {
    company_name?: string
    contact_first_name?: string
    contact_last_name?: string
  }
  property?: {
    name?: string
    address?: string
  }
  contract_id?: string
  is_recurring?: boolean
  scheduled_start_time?: string
  scheduled_end_time?: string
}

const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  draft: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800' },
  scheduled: { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800' },
  in_progress: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' },
  completed: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800' },
  cancelled: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800' },
  pending_review: { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' },
}

const PRIORITY_DOTS: Record<string, string> = {
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500',
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function JobCalendar() {
  const navigate = useNavigate()
  const { currentDivision } = useDivision()
  const { user } = useAuth()
  const { can, isAdmin, loading: permLoading } = usePermissions()
  const viewAllJobs = canViewAllJobs(can, isAdmin)
  const canEditJob = can('jobs.edit')

  const [currentDate, setCurrentDate] = useState(new Date())
  const [jobs, setJobs] = useState<CalendarJob[]>([])
  const [monthStats, setMonthStats] = useState<CalendarMonthStats>(EMPTY_CALENDAR_MONTH_STATS)
  const [loading, setLoading] = useState(true)
  const [selectedJob, setSelectedJob] = useState<CalendarJob | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [customerFilter, setCustomerFilter] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const startingDayOfWeek = new Date(year, month, 1).getDay()

  const fetchJobs = useCallback(async () => {
    if (permLoading) return
    try {
      setLoading(true)
      const rangeStart = formatDateInputValue(new Date(year, month, 1))
      const rangeEnd = formatDateInputValue(new Date(year, month + 1, 0))

      const jobSelect =
        'id, job_number, title, scheduled_date, status, priority, service_type, operational_division_id, contract_id, is_recurring, scheduled_start_time, scheduled_end_time, assigned_crew_id, assigned_employee_id, crew:crews(name), employee:employees(first_name, last_name), customer:customers(company_name, contact_first_name, contact_last_name), property:properties(name, address)'

      const buildQuery = () => {
        let query = supabase
          .from('jobs')
          .select(jobSelect)
          .gte('scheduled_date', rangeStart)
          .lte('scheduled_date', rangeEnd)
          .order('scheduled_date', { ascending: true })

        if (statusFilter === 'active') {
          query = query.in('status', ['draft', 'scheduled', 'in_progress', 'pending_review'])
        } else if (statusFilter !== 'all') {
          query = query.eq('status', statusFilter)
        }

        if (visibilityOrFilter) {
          query = query.or(visibilityOrFilter)
        }

        return query
      }

      let visibilityOrFilter: string | null = null
      let extraJobIds: string[] = []
      if (!viewAllJobs) {
        if (!user?.id) {
          setJobs([])
          setMonthStats(EMPTY_CALENDAR_MONTH_STATS)
          return
        }
        const empId = await resolveEmployeeIdForUser(user.id)
        if (!empId) {
          setJobs([])
          setMonthStats(EMPTY_CALENDAR_MONTH_STATS)
          return
        }
        const scope = await resolveAssignedJobsScope(empId, {
          scheduledDateFrom: rangeStart,
          scheduledDateTo: rangeEnd,
        })
        visibilityOrFilter = scope.orFilter
        extraJobIds = scope.extraJobIds
      }

      const [data, stats] = await Promise.all([
        fetchAllCalendarJobs<any>(buildQuery),
        fetchCalendarMonthStats({
          rangeStart,
          rangeEnd,
          division: currentDivision,
          statusFilter,
          visibilityOrFilter,
        }),
      ])
      setMonthStats(stats)

      const byId = new Map<string, any>()
      for (const row of data || []) byId.set(row.id, row)

      if (extraJobIds.length > 0) {
        const extras = await fetchJobsByIds(extraJobIds, jobSelect)
        for (const row of extras) {
          const scheduled = String(row.scheduled_date || '')
          if (scheduled < rangeStart || scheduled > rangeEnd) continue
          if (statusFilter === 'active') {
            if (
              !['draft', 'scheduled', 'in_progress', 'pending_review'].includes(
                String(row.status || '')
              )
            ) {
              continue
            }
          } else if (statusFilter !== 'all' && row.status !== statusFilter) {
            continue
          }
          if (typeof row.id === 'string') byId.set(row.id, row)
        }
      }

      const mapped = Array.from(byId.values())
        .map((row: any) => ({
          ...row,
          customer: Array.isArray(row.customer) ? row.customer[0] || null : row.customer,
          property: Array.isArray(row.property) ? row.property[0] || null : row.property,
          crew: Array.isArray(row.crew) ? row.crew[0] || null : row.crew,
          employee: Array.isArray(row.employee) ? row.employee[0] || null : row.employee,
        }))
        .filter((job) => recordMatchesDivision(job, currentDivision)) as CalendarJob[]

      const ids = mapped.map((j) => j.id)
      const teamByJob: Record<string, string> = {}
      if (ids.length > 0) {
        const { data: assignRows } = await supabase
          .from('job_assignments')
          .select(
            `job_id, crew:crews(name), employee:employees(first_name, last_name)`
          )
          .in('job_id', ids)
          .is('unassigned_at', null)
        const grouped: Record<string, any[]> = {}
        for (const row of assignRows || []) {
          const r = {
            ...row,
            crew: Array.isArray(row.crew) ? row.crew[0] || null : row.crew,
            employee: Array.isArray(row.employee) ? row.employee[0] || null : row.employee,
          }
          if (!grouped[r.job_id]) grouped[r.job_id] = []
          grouped[r.job_id].push(r)
        }
        Object.keys(grouped).forEach((jobId) => {
          teamByJob[jobId] = formatAssigneeTeamLabel(grouped[jobId])
        })
      }

      setJobs(
        mapped.map((j) => ({
          ...j,
          teamLabel:
            teamByJob[j.id] ||
            j.crew?.name ||
            (j.employee
              ? `${j.employee.first_name || ''} ${j.employee.last_name || ''}`.trim()
              : '') ||
            undefined,
        }))
      )
    } catch (err) {
      console.error('Error fetching calendar jobs:', err)
    } finally {
      setLoading(false)
    }
  }, [year, month, statusFilter, currentDivision, viewAllJobs, permLoading, user?.id])

  useEffect(() => { fetchJobs() }, [fetchJobs])

  const handleDayClick = (dateStr: string) => {
    navigate(`/dispatch/${dateStr}`)
  }

  const matchesCustomerFilter = useCallback(
    (job: CalendarJob) => {
      if (!customerFilter) return true
      const name =
        job.customer?.company_name ||
        `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim()
      return name.toLowerCase().includes(customerFilter.toLowerCase())
    },
    [customerFilter]
  )

  const filteredCalendarJobs = useMemo(
    () => jobs.filter(matchesCustomerFilter),
    [jobs, matchesCustomerFilter]
  )

  const getJobsForDate = (dateStr: string) =>
    filteredCalendarJobs.filter((j) => String(j.scheduled_date || '').slice(0, 10) === dateStr)

  const isToday = (d: Date) => {
    const today = new Date()
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()
  }

  const isPast = (d: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const check = new Date(d)
    check.setHours(0, 0, 0, 0)
    return check < today
  }

  const getCustomerName = (job: CalendarJob) =>
    job.customer?.company_name ||
    `${job.customer?.contact_first_name || ''} ${job.customer?.contact_last_name || ''}`.trim() ||
    'N/A'

  const getStatusStyle = (status: string) => STATUS_COLORS[status] || STATUS_COLORS.draft

  const displayStats = useMemo(() => {
    if (customerFilter) {
      return {
        total: filteredCalendarJobs.length,
        scheduled: filteredCalendarJobs.filter((j) => j.status === 'scheduled').length,
        inProgress: filteredCalendarJobs.filter((j) => j.status === 'in_progress').length,
        completed: filteredCalendarJobs.filter((j) => isFieldCompleteStatus(j.status)).length,
      }
    }
    return monthStats
  }, [customerFilter, filteredCalendarJobs, monthStats])

  const totalJobsThisMonth = displayStats.total
  const scheduledCount = displayStats.scheduled
  const inProgressCount = displayStats.inProgress
  const completedCount = displayStats.completed

  const renderCalendarDays = () => {
    const days = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="min-h-[110px] border border-gray-100 bg-gray-50/50" />)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      const dateStr = formatDateInputValue(date)
      const cellJobs = getJobsForDate(dateStr)
      const currentDay = isToday(date)
      const pastDate = isPast(date)

      days.push(
        <div
          key={day}
          onClick={() => handleDayClick(dateStr)}
          className={`min-h-[110px] border p-1.5 transition-colors cursor-pointer ${
            currentDay
              ? 'bg-blue-50/70 border-blue-300'
              : pastDate
              ? 'bg-gray-50/50 border-gray-200'
              : 'bg-white border-gray-200 hover:bg-gray-50/50'
          }`}
        >
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${
                currentDay ? 'bg-blue-600 text-white'
                : pastDate ? 'text-gray-400'
                : 'text-gray-900'
              }`}
            >
              {day}
            </span>
            {cellJobs.length > 0 && (
              <span className="text-[10px] text-gray-400 font-medium">{cellJobs.length}</span>
            )}
          </div>

          <div className="space-y-0.5 overflow-y-auto max-h-[70px]">
            {cellJobs.slice(0, 4).map((job) => {
              const style = getStatusStyle(job.status)
              const assignee = job.teamLabel ||
                job.crew?.name ||
                (job.employee
                  ? `${job.employee.first_name || ''} ${job.employee.last_name || ''}`.trim()
                  : '')
              return (
                <button
                  key={job.id}
                  onClick={(e) => { e.stopPropagation(); setSelectedJob(job) }}
                  className={`w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded border truncate ${style.bg} ${style.border} ${style.text} hover:opacity-80 transition-opacity`}
                  title={`${job.job_number}: ${job.title} — ${getCustomerName(job)}${assignee ? ` · ${assignee}` : ' · Unassigned'}`}
                >
                  <span className="font-semibold">#{job.job_number}</span>{' '}
                  <span className="opacity-75">{job.title.length > 12 ? job.title.substring(0, 10) + '…' : job.title}</span>
                  {assignee ? (
                    <span className="opacity-60"> · {assignee.length > 10 ? assignee.slice(0, 8) + '…' : assignee}</span>
                  ) : (
                    <span className="opacity-50"> · ?</span>
                  )}
                </button>
              )
            })}
            {cellJobs.length > 4 && (
              <div className="text-[9px] text-gray-500 text-center font-medium">
                +{cellJobs.length - 4} more
              </div>
            )}
          </div>
        </div>
      )
    }

    return days
  }

  const busyDays = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1
    const date = new Date(year, month, day)
    const dateStr = formatDateInputValue(date)
    const dayJobs = getJobsForDate(dateStr)
    return { day, date, dateStr, count: dayJobs.length, jobs: dayJobs }
  }).filter((d) => d.count > 0)

  return (
    <MobilePage>
      <MobilePageHeader
        title="Dispatch Calendar"
        subtitle={<span className="hidden sm:inline">Click a day to view and manage that day&apos;s schedule</span>}
        actions={
          <button
            type="button"
            onClick={() => navigate('/jobs/create')}
            className={mobile.primaryBtn}
          >
            <span className="sm:hidden">New</span>
            <span className="hidden sm:inline">+ New Job</span>
          </button>
        }
      />

      <MobileStatStrip
        className="md:hidden"
        stats={[
          { key: 'total', label: 'This Month', value: totalJobsThisMonth },
          { key: 'scheduled', label: 'Scheduled', value: scheduledCount, tone: 'amber' },
          { key: 'progress', label: 'In Progress', value: inProgressCount, tone: 'blue' },
          { key: 'done', label: 'Completed', value: completedCount, tone: 'green' },
        ]}
      />

      <div className="hidden md:grid grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-gray-900">{totalJobsThisMonth}</p>
          <p className="text-xs text-gray-500 mt-1">Total This Month</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-yellow-600">{scheduledCount}</p>
          <p className="text-xs text-gray-500 mt-1">Scheduled</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-blue-600">{inProgressCount}</p>
          <p className="text-xs text-gray-500 mt-1">In Progress</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-center">
          <p className="text-2xl font-bold text-green-600">{completedCount}</p>
          <p className="text-xs text-gray-500 mt-1">Completed</p>
        </div>
      </div>

      <div className={`${mobile.surface} overflow-hidden`}>
        <div className="flex flex-col gap-3 p-3 sm:p-4 border-b border-gray-200 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1.5 min-w-0">
            <button
              type="button"
              onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
              className={mobile.ghostBtn}
              aria-label="Previous month"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <h2 className="text-base sm:text-xl font-semibold text-gray-900 min-w-0 flex-1 text-center truncate">
              {MONTH_NAMES[month]} {year}
            </h2>
            <button
              type="button"
              onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
              className={mobile.ghostBtn}
              aria-label="Next month"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setCurrentDate(new Date())}
              className={`${mobile.secondaryBtn} min-h-[44px] px-3 text-sm`}
            >
              Today
            </button>
          </div>
          <div className="flex items-center gap-2">
            <MobileSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 sm:max-w-[11rem]"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </MobileSelect>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`${mobile.ghostBtn} ${showFilters ? 'bg-blue-100 text-blue-600' : ''}`}
              aria-label="Filters"
            >
              <FilterIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-3 sm:p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <label className={mobile.label}>Customer</label>
                <input
                  type="text"
                  placeholder="Filter by customer..."
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className={mobile.field}
                />
              </div>
              {customerFilter && (
                <button
                  type="button"
                  onClick={() => setCustomerFilter('')}
                  className="min-h-[48px] text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Legend — scroll on narrow screens */}
        <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2 border-b border-gray-200 text-xs overflow-x-auto">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded" />
            <span className="text-gray-600">Scheduled</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded" />
            <span className="text-gray-600">In Progress</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded" />
            <span className="text-gray-600">Completed</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-3 h-3 bg-purple-100 border border-purple-300 rounded" />
            <span className="text-gray-600">Pending Review</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <div className="w-3 h-3 bg-red-100 border border-red-300 rounded" />
            <span className="text-gray-600">Cancelled</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Mobile: day chips + busy-day list */}
            <div className="native-mobile-list p-3 space-y-3">
              <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 [-webkit-overflow-scrolling:touch]">
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1
                  const date = new Date(year, month, day)
                  const dateStr = formatDateInputValue(date)
                  const count = getJobsForDate(dateStr).length
                  const currentDay = isToday(date)
                  return (
                    <button
                      key={dateStr}
                      type="button"
                      onClick={() => handleDayClick(dateStr)}
                      className={`snap-start flex-shrink-0 w-12 min-h-[56px] rounded-xl border flex flex-col items-center justify-center gap-0.5 ${
                        currentDay
                          ? 'bg-blue-600 text-white border-blue-600'
                          : count > 0
                            ? 'bg-white border-blue-200 text-gray-900'
                            : 'bg-gray-50 border-gray-200 text-gray-400'
                      }`}
                    >
                      <span className="text-[10px] font-medium uppercase opacity-80">
                        {DAY_NAMES[date.getDay()].slice(0, 1)}
                      </span>
                      <span className="text-sm font-bold tabular-nums">{day}</span>
                      {count > 0 && (
                        <span
                          className={`text-[10px] font-semibold tabular-nums ${
                            currentDay ? 'text-blue-100' : 'text-blue-600'
                          }`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>

              {busyDays.length === 0 ? (
                <MobileEmptyState
                  icon={<CalendarIcon className="w-12 h-12" />}
                  title="No jobs this month"
                  description="Adjust filters or create a job to fill the calendar."
                />
              ) : (
                <div className={`${mobile.listGroup}`}>
                  {busyDays.map(({ dateStr, date, count, jobs: dayJobs }) => (
                    <button
                      key={dateStr}
                      type="button"
                      onClick={() => handleDayClick(dateStr)}
                      className="w-full text-left px-4 py-3.5 min-h-[52px] active:bg-gray-50"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">
                            {date.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {dayJobs
                              .slice(0, 2)
                              .map((j) => j.title)
                              .join(' · ')}
                            {count > 2 ? ` · +${count - 2} more` : ''}
                          </p>
                        </div>
                        <span className={`${mobile.badge} bg-blue-100 text-blue-800 flex-shrink-0`}>
                          {count} job{count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop month grid */}
            <div className="native-desktop-table">
              <div className="grid grid-cols-7">
                {DAY_NAMES.map((day) => (
                  <div
                    key={day}
                    className="bg-gray-100 p-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200"
                  >
                    {day}
                  </div>
                ))}
                {renderCalendarDays()}
              </div>
            </div>
          </>
        )}
      </div>

      {selectedJob && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          onClick={() => setSelectedJob(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-gray-500">#{selectedJob.job_number}</span>
                    {selectedJob.is_recurring && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-medium">
                        Recurring
                      </span>
                    )}
                    {selectedJob.priority && (
                      <span
                        className={`w-2.5 h-2.5 rounded-full ${PRIORITY_DOTS[selectedJob.priority] || 'bg-gray-400'}`}
                        title={selectedJob.priority}
                      />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedJob.title}</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedJob(null)}
                  className={`${mobile.ghostBtn}`}
                >
                  <XIcon className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-3 mb-5">
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusStyle(selectedJob.status).bg} ${getStatusStyle(selectedJob.status).text}`}
                  >
                    {selectedJob.status.replace(/_/g, ' ')}
                  </span>
                  {selectedJob.service_type && (
                    <span className="text-xs text-gray-500">{selectedJob.service_type}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <UserIcon className="w-4 h-4 text-gray-400" />
                  {getCustomerName(selectedJob)}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <UserIcon className="w-4 h-4 text-gray-400" />
                  <span>
                    Assigned:{' '}
                    {selectedJob.crew?.name ||
                      (selectedJob.employee
                        ? `${selectedJob.employee.first_name || ''} ${selectedJob.employee.last_name || ''}`.trim()
                        : null) || <span className="text-amber-700 font-medium">Unassigned</span>}
                  </span>
                </div>
                {selectedJob.property?.address && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPinIcon className="w-4 h-4 text-gray-400" />
                    {selectedJob.property.name ? `${selectedJob.property.name} — ` : ''}
                    {selectedJob.property.address}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="text-gray-400 text-xs font-medium">DATE</span>
                  {new Date(selectedJob.scheduled_date + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
                {(selectedJob.scheduled_start_time || selectedJob.scheduled_end_time) && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="text-gray-400 text-xs font-medium">TIME</span>
                    {selectedJob.scheduled_start_time || '—'} – {selectedJob.scheduled_end_time || '—'}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedJob(null)
                    navigate(`/jobs/details/${selectedJob.id}`)
                  }}
                  className={`${mobile.primaryBtn} flex-1`}
                >
                  <EyeIcon className="w-4 h-4" /> View Details
                </button>
                {canEditJob && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedJob(null)
                      navigate(`/jobs/edit/${selectedJob.id}`)
                    }}
                    className={`${mobile.secondaryBtn} flex-1`}
                  >
                    <EditIcon className="w-4 h-4" /> Edit Job
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
